/**
 * /crm/tickets — the triage queue.
 *
 * Built on `DataView` like every other list page in BI, which brings
 * whole-row navigation, keyboard support, sorting, a page-size selector,
 * CSV export and first-class loading/error/empty states.
 *
 * Filters live in the query string so a filtered queue can be shared in
 * Slack and survives the back button from a ticket. The status rail is a
 * single set of chips: the old "Open only" checkbox meant the way to see
 * resolved tickets was to uncheck a box, and it silently contradicted the
 * status dropdown.
 *
 * Rows stay one line tall. The order timeline that used to be embedded in
 * every row is on the ticket and the order pages, where there is room for
 * it; the queue instead answers "what should I pick up next" — who owns
 * it, how stale it is, and which stage broke.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LifeBuoy, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ToggleChip } from '@/components/ui/Chip';
import { DataView, downloadCsv } from '@/components/data/DataView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { CrmCreateDrawer } from '@/components/crm/CrmCreateDrawer';
import { useAuth } from '@/auth/AuthProvider';
import {
  useCrmTickets,
  type CrmTicket,
  type CrmTicketPriority,
  type CrmTicketStatus,
  type CrmTicketType,
} from '@/api/bi/crm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_TONE,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  TICKET_TYPE_LABEL,
  TICKET_TYPE_TONE,
} from '@/lib/crmStatus';
import { cn } from '@/lib/cn';
import { fmt } from '@/lib/format';
import { slaTagLabel } from '@/lib/slaTags';

const TYPE_TABS: Array<{ value: CrmTicketType | 'all'; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'sla_breach', label: 'SLA' },
  { value: 'customer_delay', label: 'Customer Delay' },
  { value: 'refund', label: 'Refunds' },
  { value: 'shrinkage', label: 'Shrinkage' },
  { value: 'cancellation', label: 'Cancellations' },
  { value: 'channel_escalation', label: 'Channel' },
  { value: 'damage', label: 'Cycle Damage' },
];

/**
 * One rail instead of a checkbox plus a dropdown. `unresolved` is the
 * union of the open statuses — the default view of a queue.
 */
type StatusFilter = 'all' | 'unresolved' | CrmTicketStatus;

const STATUS_CHIPS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'open', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const PRIORITY_OPTIONS: Array<{ value: CrmTicketPriority | 'all'; label: string }> = [
  { value: 'all', label: 'Any priority' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const DEFAULT_STATUS: StatusFilter = 'unresolved';
const STATUS_VALUES = new Set<string>(STATUS_CHIPS.map((c) => c.value));
const TYPE_VALUES = new Set<string>(TYPE_TABS.map((t) => t.value));
const PRIORITY_VALUES = new Set<string>(PRIORITY_OPTIONS.map((p) => p.value));

export function CrmTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  const typeParam = searchParams.get('type') ?? 'all';
  const type = (TYPE_VALUES.has(typeParam) ? typeParam : 'all') as CrmTicketType | 'all';
  const statusParam = searchParams.get('status') ?? DEFAULT_STATUS;
  const status = (STATUS_VALUES.has(statusParam) ? statusParam : DEFAULT_STATUS) as StatusFilter;
  const priorityParam = searchParams.get('priority') ?? 'all';
  const priority = (
    PRIORITY_VALUES.has(priorityParam) ? priorityParam : 'all'
  ) as CrmTicketPriority | 'all';
  const mineOnly = searchParams.get('mine') === '1';
  const q = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1);
  const pageSize = Math.max(1, Number(searchParams.get('page_size') ?? 25) || 25);

  /** Every filter write resets to page 1 unless the page itself changed. */
  const setParams = useCallback(
    (next: Record<string, string | null>, keepPage = false) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          }
          if (!keepPage) params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The search box types locally and settles into the URL, so the term is
  // still shareable without rewriting history on every keystroke.
  const [searchInput, setSearchInput] = useState(q);
  const debouncedQ = useDebouncedValue(searchInput.trim(), 300);

  useEffect(() => {
    if (debouncedQ === (searchParams.get('q') ?? '')) return;
    setParams({ q: debouncedQ || null });
    // Reacting to anything else here would fight the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  // Adopt a term that arrived from outside — back button, or a pasted link.
  useEffect(() => {
    setSearchInput((prev) => (prev.trim() === q.trim() ? prev : q));
  }, [q]);

  const tickets = useCrmTickets({
    type: type === 'all' ? null : type,
    status: status === 'all' || status === 'unresolved' ? null : status,
    priority: priority === 'all' ? null : priority,
    assigneeId: mineOnly ? user?.id ?? null : null,
    isOpen: status === 'unresolved',
    q: debouncedQ || null,
    page,
    pageSize,
  });

  const rows = tickets.data?.results ?? [];
  const pagination = tickets.data?.pagination;

  const hasFilters =
    type !== 'all' ||
    status !== DEFAULT_STATUS ||
    priority !== 'all' ||
    mineOnly ||
    Boolean(q.trim());

  const columns = useMemo<ColumnDef<CrmTicket, unknown>[]>(
    () => [
      {
        id: 'ticket',
        header: 'Ticket',
        accessorFn: (t) => t.summary || TICKET_TYPE_LABEL[t.type],
        cell: ({ row }) => {
          const t = row.original;
          return (
            <div className="min-w-0 max-w-[22rem]">
              <div className="flex items-center gap-1.5">
                <span className="font-medium tabular-nums text-muted-foreground">#{t.id}</span>
                <span className="truncate font-medium">
                  {t.summary || TICKET_TYPE_LABEL[t.type]}
                </span>
                {t.is_auto_created ? <Badge tone="info">Auto</Badge> : null}
              </div>
              {t.interaction_count ? (
                <p className="text-[11px] text-muted-foreground">
                  {t.interaction_count} interaction{t.interaction_count === 1 ? '' : 's'}
                </p>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (t) => TICKET_TYPE_LABEL[t.type],
        cell: ({ row }) => (
          <Badge tone={TICKET_TYPE_TONE[row.original.type]}>
            {TICKET_TYPE_LABEL[row.original.type]}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (t) => TICKET_STATUS_LABEL[t.status],
        cell: ({ row }) => (
          <Badge tone={TICKET_STATUS_TONE[row.original.status]}>
            {TICKET_STATUS_LABEL[row.original.status]}
          </Badge>
        ),
      },
      {
        id: 'priority',
        header: 'Priority',
        accessorFn: (t) => PRIORITY_RANK[t.priority],
        cell: ({ row }) => (
          <Badge tone={TICKET_PRIORITY_TONE[row.original.priority]}>
            {TICKET_PRIORITY_LABEL[row.original.priority]}
          </Badge>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (t) => ownerLabel(t),
        cell: ({ row }) => {
          const t = row.original;
          const owner = ownerOf(t);
          if (owner.count === 0) return <Badge tone="warning">Unassigned</Badge>;
          if (!owner.name) {
            return (
              <span className="text-muted-foreground">
                {owner.count} assigned
              </span>
            );
          }
          const extra = owner.count - 1;
          return (
            <span className="inline-flex items-center gap-1">
              <span className="truncate">{owner.name}</span>
              {extra > 0 ? (
                <span className="text-[11px] text-muted-foreground">+{extra}</span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'stage',
        header: 'SLA stage',
        accessorFn: (t) => (t.breach_tag?.tag ? slaTagLabel(t.breach_tag.tag) : ''),
        cell: ({ row }) => {
          const tag = row.original.breach_tag;
          if (!tag?.tag) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="inline-flex flex-col">
              <span>{slaTagLabel(tag.tag)}</span>
              {tag.late_duration ? (
                <span className="text-[11px] text-muted-foreground">
                  late {fmt.duration(tag.late_duration)}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'order',
        header: 'Order',
        accessorFn: (t) => t.order_id ?? 0,
        cell: ({ row }) => {
          const t = row.original;
          if (t.order_id == null) return <span className="text-muted-foreground">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/orders/${t.order_id}`);
              }}
              className="press rounded-sm font-medium text-primary hover:underline focus-ring"
            >
              #{t.order_id}
            </button>
          );
        },
      },
      {
        id: 'store',
        header: 'Store',
        accessorFn: (t) => t.store_name ?? '',
        cell: ({ row }) => (
          <span className="truncate">{row.original.store_name ?? '—'}</span>
        ),
      },
      {
        id: 'age',
        header: 'Age',
        accessorFn: (t) => (t.created_at ? new Date(t.created_at).getTime() : 0),
        cell: ({ row }) => <AgeCell ticket={row.original} />,
      },
    ],
    [navigate],
  );

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Tickets"
        description="Every support ticket — SLA breaches, customer delays, refunds, shrinkage."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
            <LifeBuoy className="h-3.5 w-3.5" /> New ticket
          </Button>
        }
      />

      <Tabs value={type} onValueChange={(v) => setParams({ type: v === 'all' ? null : v })}>
        <TabsList className="mb-3 flex flex-wrap">
          {TYPE_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {STATUS_CHIPS.map((chip) => (
          <ToggleChip
            key={chip.value}
            active={status === chip.value}
            onClick={() => setParams({ status: chip.value === DEFAULT_STATUS ? null : chip.value })}
          >
            {chip.label}
          </ToggleChip>
        ))}
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ToggleChip active={mineOnly} onClick={() => setParams({ mine: mineOnly ? null : '1' })}>
          Assigned to me
        </ToggleChip>
        {hasFilters ? (
          <ToggleChip
            active={false}
            icon={<X className="h-3 w-3" aria-hidden />}
            onClick={() => setParams({ type: null, status: null, priority: null, mine: null, q: null })}
          >
            Clear filters
          </ToggleChip>
        ) : null}
      </div>

      <DataView<CrmTicket>
        data={rows}
        columns={columns}
        title="Queue"
        description={
          pagination
            ? `${pagination.total.toLocaleString()} ticket${pagination.total === 1 ? '' : 's'} match`
            : undefined
        }
        search={{
          value: searchInput,
          onChange: setSearchInput,
          // Phone match (customer, order snapshot, order staff, assignees)
          // is applied by Django — see backend/docs/DROPIT_TICKET_PHONE_SEARCH.md.
          placeholder: 'Search summary, order, customer, phone…',
        }}
        toolbar={
          <Select
            value={priority}
            onValueChange={(v) => setParams({ priority: v === 'all' ? null : v })}
          >
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        pagination={
          pagination
            ? {
                page: pagination.page,
                pageSize: pagination.page_size,
                total: pagination.total,
                onPageChange: (next) => setParams({ page: String(next) }, true),
                onPageSizeChange: (next) => setParams({ page_size: String(next), page: null }),
              }
            : undefined
        }
        // A full page of tickets outruns the viewport, and the columns are
        // mostly short codes that mean nothing once the header scrolls away.
        maxHeight="62vh"
        stickyFirstColumn
        columnToggles
        isLoading={tickets.isLoading}
        isError={tickets.isError}
        onRetry={() => void tickets.refetch()}
        onRowClick={(row) => navigate(`/crm/tickets/${row.id}`)}
        onExportCsv={() => downloadCsv('crm-tickets', rows.map(toCsvRow), CSV_HEADERS)}
        emptyTitle="No tickets match"
        emptyDescription={
          hasFilters
            ? 'Nothing in this slice. Clear the filters to see the whole queue.'
            : 'Nothing open right now — the queue is clear.'
        }
        density="compact"
      />

      <CrmCreateDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        orderId={null}
        defaultType={type === 'all' || type === 'channel_escalation' ? 'customer_delay' : type}
      />
    </>
  );
}

const PRIORITY_RANK: Record<CrmTicketPriority, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

/**
 * Who owns the ticket. List rows carry `assignment_count` but not always
 * the assignment objects, so fall back to the count when there is no name
 * to show — "2 assigned" is still worlds away from showing nothing.
 */
function ownerOf(ticket: CrmTicket): { name: string | null; count: number } {
  const assignments = ticket.assignments ?? [];
  const primary = assignments.find((a) => a.is_primary) ?? assignments[0];
  const count = ticket.assignment_count ?? assignments.length;
  return { name: primary?.user?.name ?? null, count };
}

function ownerLabel(ticket: CrmTicket): string {
  const { name, count } = ownerOf(ticket);
  if (name) return name;
  return count > 0 ? `${count} assigned` : '';
}

/** Hours since a timestamp, or null when it is missing. */
function hoursSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms)) return null;
  return ms / 3_600_000;
}

function AgeCell({ ticket }: { ticket: CrmTicket }) {
  const age = hoursSince(ticket.created_at);
  const isOpen = !['resolved', 'rejected'].includes(ticket.status);
  const stale = isOpen && age != null && age >= 24;
  return (
    <span className="inline-flex flex-col text-right">
      <span className={cn('tabular-nums', stale && 'font-medium text-warning-soft')}>
        {fmt.ago(ticket.created_at)}
      </span>
      {ticket.updated_at && ticket.updated_at !== ticket.created_at ? (
        <span className="text-[11px] text-muted-foreground">
          touched {fmt.ago(ticket.updated_at)}
        </span>
      ) : null}
    </span>
  );
}

const CSV_HEADERS = [
  { key: 'id' as const, label: 'Ticket' },
  { key: 'type' as const, label: 'Type' },
  { key: 'summary' as const, label: 'Summary' },
  { key: 'status' as const, label: 'Status' },
  { key: 'priority' as const, label: 'Priority' },
  { key: 'owner' as const, label: 'Owner' },
  { key: 'sla_stage' as const, label: 'SLA stage' },
  { key: 'order_id' as const, label: 'Order' },
  { key: 'store' as const, label: 'Store' },
  { key: 'created_at' as const, label: 'Created' },
  { key: 'updated_at' as const, label: 'Updated' },
];

function toCsvRow(ticket: CrmTicket) {
  return {
    id: ticket.id,
    type: TICKET_TYPE_LABEL[ticket.type],
    summary: ticket.summary || TICKET_TYPE_LABEL[ticket.type],
    status: TICKET_STATUS_LABEL[ticket.status],
    priority: TICKET_PRIORITY_LABEL[ticket.priority],
    owner: ownerLabel(ticket) || 'Unassigned',
    sla_stage: ticket.breach_tag?.tag ? slaTagLabel(ticket.breach_tag.tag) : '',
    order_id: ticket.order_id ?? '',
    store: ticket.store_name ?? '',
    created_at: ticket.created_at,
    updated_at: ticket.updated_at,
  };
}
